// Conversation starters and follow-up prompts for Chat AI.
//
// Saran disusun berlapis:
//   1) BASE_SUGGESTIONS  → universal, dipakai saat tidak ada konteks lain.
//   2) OVERVIEW_SUGGESTIONS → saat user membuka layar Overview department.
//   3) REPORT_SUGGESTIONS_BY_ID → saran spesifik per sub-report Digital Marketing
//      (mengacu metrik nyata yang di-parse oleh src/utils/parseWebTraffic.js,
//      parseWAGrowth.js, dan struktur SUBSEGMENT_TO_REPORT di workflowMapping.js).
//
// Hanya Digital Marketing yang aktif di production saat ini. Department lain
// (sales, finance, dst.) sengaja tidak diberi saran karena belum punya data
// laporan yang nyata — tambahkan saat sub-report mereka diaktifkan.

export const BASE_SUGGESTIONS = [
  'Apa insight utama dari laporan ini?',
  'Apa rekomendasi action yang perlu diambil?',
  'Jelaskan tren penting yang terlihat.',
]

export const OVERVIEW_SUGGESTIONS = [
  'Apa highlight utama overview Digital Marketing hari ini?',
  'Sub-report mana yang paling perlu perhatian segera?',
  'Ringkas kondisi WA Growth, Web Traffic, dan Acquisition Funnel dalam 3 poin.',
]

// Starter prompt khusus Google Ads — kalimatnya sengaja konkret & deterministik
// agar AI langsung memicu tool live (get_google_ads_summary / ads_health_check).
// Ditampilkan permanen di empty state karena ini kapabilitas unggulan baru.
export const GOOGLE_ADS_SUGGESTIONS = [
  'Tampilkan performa Google Ads 30 hari terakhir, breakdown per campaign.',
  'Campaign mana yang spend-nya tinggi tapi konversinya rendah?',
  'Bandingkan CTR & CPC antar campaign minggu ini.',
  'Cek koneksi Google Ads, Meta Ads, dan GA4 sekarang.',
]

// Saran per sub-report Digital Marketing.
// Pertanyaan menyebut metrik real agar AI bisa langsung menggali angka di laporan.
export const REPORT_SUGGESTIONS_BY_ID = {
  // REPORT 1 — Whatsapp Growth Metrics
  // Metrik: Total Tickets, New Customer, Returning Customer, Sales Hunt, Sales Followup
  'wa-growth': [
    'Bagaimana tren Total Tickets vs rolling 30-day average?',
    'Hari mana yang menunjukkan drop terbesar pada New Customer?',
    'Bandingkan kontribusi Sales Hunt vs Sales Followup minggu ini.',
    'Apakah Returning Customer naik atau turun? Apa kemungkinan penyebabnya?',
  ],

  // REPORT 2 — Web Traffic 30 hari terakhir
  // Metrik: Total Sessions, Returning Visitors, Organic, Ads, Direct, Other + Contact Click ratio
  'web-traffic': [
    'Channel trafik mana yang paling banyak menyumbang Contact Click?',
    'Bandingkan ratio Contact Click antara Organic, Ads, dan Direct.',
    'Apakah trafik dari Ads sepadan dengan jumlah Contact Click yang dihasilkan?',
    'Hari mana yang punya Total Sessions tertinggi dalam 30 hari ini?',
  ],

  // REPORT 3 — Web Traffic UTM Detailed 15 hari
  'web-traffic-utm': [
    'Kombinasi utm_source / utm_medium / utm_campaign mana yang performanya paling baik?',
    'Adakah campaign UTM yang memboroskan trafik tanpa menghasilkan Contact Click?',
    'Bandingkan performa campaign 15 hari ini vs sebelumnya.',
    'Identifikasi UTM yang nilainya turun drastis — apa kemungkinan penyebabnya?',
  ],

  // REPORT 4 — Customer Acquisition & Growth Funnel — Penjualan 30 hari
  'acquisition-funnel': [
    'Di stage funnel mana drop-off paling besar terjadi?',
    'Berapa conversion rate dari awareness ke acquisition periode ini?',
    'Identifikasi bottleneck utama di funnel akuisisi.',
    'Saran konkret untuk meningkatkan konversi di stage paling lemah.',
  ],

  // REPORT 5 — Top 30 /promo/ Landing Pages 30 hari
  'top-landing': [
    'Landing page /promo/ mana yang punya engagement paling tinggi?',
    'Adakah landing page promo yang trafiknya tinggi tapi conversion rendah?',
    'Bandingkan performa top 5 landing pages — apa kesamaan polanya?',
    'Halaman promo mana yang patut dioptimasi atau di-pause?',
  ],
}

// ── Public API ─────────────────────────────────────────────────────────────
//
// Catatan: follow-up prompts SETELAH AI menjawab TIDAK lagi punya fallback
// template di FE. Sekarang murni dari blok <follow-ups> yang AI generate
// kontekstual ke jawabannya. Kalau AI tidak emit (mis. session lama atau
// jawaban non-data), strip follow-up bar tidak ditampilkan — lebih baik
// kosong daripada menyodorkan kalimat generik yang tidak nyambung.

/**
 * @param {boolean} isOverview
 * @returns {string[]}
 */
export function getBaseSuggestions(isOverview) {
  return isOverview ? OVERVIEW_SUGGESTIONS : BASE_SUGGESTIONS
}

/**
 * Saran kontekstual yang bergantung pada sub-report yang sedang aktif.
 * Jika sedang di Overview atau report tidak dikenali, kembalikan array kosong.
 *
 * @param {string|undefined} _deptId   - reserved (saat ini hanya digital-marketing)
 * @param {string|undefined} reportId  - id sub-report (mis. 'wa-growth', 'web-traffic')
 * @param {boolean} isOverview         - true saat user membuka Overview department
 * @returns {string[]}
 */
export function getDomainSuggestions(_deptId, reportId, isOverview) {
  if (isOverview) return []
  if (!reportId) return []
  return REPORT_SUGGESTIONS_BY_ID[reportId] || []
}
